import React, { useEffect, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { useTranslation } from 'react-i18next'

import { Button } from '../Button'

import { useEventListener } from '@utils/hooks/useEventListener'

import { Card, CardContent, CardFooter, FieldContainer, Img, Label, SignatureContainer } from './SignaturePad.styled'

interface SignaturePadProps {
  width?: number
  height?: number
  className?: string
  disabled?: boolean
  initialDataUrl?: string
  label?: string
  showClearButton?: boolean
  onSave?: (dataUrl: string | undefined) => void
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  height = 200,
  className = '',
  disabled = false,
  label,
  initialDataUrl,
  showClearButton = true,
  onSave,
}) => {
  const { t } = useTranslation()
  const signatureRef = useRef<SignatureCanvas>(null)
  const signatureWrapper = useRef<HTMLDivElement | null>(null)
  const [signatureData, setSignatureData] = useState<string | null>(initialDataUrl || null)
  const [canvasWidth, setCanvasWidth] = useState<number>(0)

  const updateCanvasWidth = () => {
    if (signatureWrapper.current) {
      setCanvasWidth(signatureWrapper.current.clientWidth)
    }
  }

  useEventListener('resize', updateCanvasWidth)

  useEffect(() => {
    updateCanvasWidth()
  }, [])

  useEffect(() => {
    if (initialDataUrl) {
      signatureRef.current?.off()
      setSignatureData(initialDataUrl)
    }
    if (disabled) {
      signatureRef.current?.off()
    }
  }, [initialDataUrl, disabled])

  const clear = (): void => {
    signatureRef.current?.clear()
    signatureRef.current?.on()
    setSignatureData(null)
    onSave && onSave(undefined)
  }

  const onEnd = (): void => {
    if (!signatureRef.current?.isEmpty()) {
      signatureRef.current?.off()
      const dataUrl = signatureRef.current?.toDataURL('image/png')
      if (dataUrl) {
        setSignatureData(dataUrl)
        onSave && onSave(dataUrl)
      }
    }
  }

  const canvasProps = {
    width: canvasWidth,
    height: height,
  }

  const isEmpty = !signatureData && (signatureRef.current?.isEmpty() ?? true)

  return (
    <FieldContainer>
      {label && <Label>{label}</Label>}
      <Card className={className} disabled={disabled}>
        <CardContent disabled={disabled}>
          <SignatureContainer ref={signatureWrapper} disabled={disabled} isEmpty={isEmpty}>
            {signatureData ? (
              <Img
                alt="signature"
                src={signatureData ? signatureData : undefined}
                height={canvasProps.height}
                width={canvasWidth || 200}
              />
            ) : (
              <SignatureCanvas ref={signatureRef} onEnd={onEnd} maxWidth={2} canvasProps={canvasProps} />
            )}
          </SignatureContainer>
        </CardContent>

        {!disabled && (
          <CardFooter>
            {showClearButton && (
              <Button variant="outline" onClick={clear} type="button">
                {t('components_SignaturePad_clear')}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
    </FieldContainer>
  )
}
